import { useState } from 'react';
import { Box, Typography, Collapse, IconButton, Link, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { colors, cardSx } from '../theme/designTheme';

const sourceMeta = {
  asic_connect: { label: 'ASIC Connect',         Icon: AccountBalanceIcon },
  corelogic:    { label: 'CoreLogic',            Icon: HomeWorkIcon },
  asic_credit:  { label: 'ASIC Credit Register', Icon: GavelIcon },
  abr_gst:      { label: 'ABR / GST',            Icon: BusinessCenterIcon },
  media_web:    { label: 'Media / Web',          Icon: PublicIcon },
};

const PayloadView = ({ payload }) => {
  if (!payload || typeof payload !== 'object') return null;
  return (
    <Box sx={{ mt: 1, p: 1.25, bgcolor: colors.surfaceElevated, border: `1px solid ${colors.borderSubtle}`, borderRadius: 1 }}>
      {Object.entries(payload).map(([k, v]) => (
        <Box key={k} sx={{ display: 'flex', gap: 1.5, py: 0.4, alignItems: 'flex-start' }}>
          <Typography sx={{
            color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase', minWidth: 140, flexShrink: 0,
          }}>
            {k.replace(/_/g, ' ')}
          </Typography>
          <Typography sx={{
            color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '11px',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const SourceCard = ({ s }) => {
  const [open, setOpen] = useState(false);
  const meta = sourceMeta[s.source] || { label: s.source, Icon: PublicIcon };
  const Icon = meta.Icon;

  return (
    <Box sx={{ borderTop: `1px solid ${colors.borderSubtle}`, '&:first-of-type': { borderTop: 'none' } }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 2, py: 1.5,
          cursor: 'pointer', '&:hover': { bgcolor: colors.surfaceHover },
        }}
      >
        <Icon sx={{ color: colors.accent, fontSize: 18, mt: 0.25, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em' }}>
              {meta.label.toUpperCase()}
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px' }}>
              {new Date(s.retrieved_at).toLocaleDateString()}
            </Typography>
          </Box>
          <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontSize: '12px', fontWeight: 600, lineHeight: 1.3 }}>
            {s.title}
          </Typography>
          {s.summary && (
            <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '11px', mt: 0.4, lineHeight: 1.45 }}>
              {s.summary}
            </Typography>
          )}
        </Box>
        {s.source_url && (
          <Link
            href={s.source_url} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ color: colors.accent, mt: 0.25 }}
          >
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        )}
        <IconButton size="small" sx={{ color: colors.textMuted, p: 0.25, mt: 0.25 }}>
          <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', fontSize: 18 }} />
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 1.5 }}>
          <PayloadView payload={s.payload} />
        </Box>
      </Collapse>
    </Box>
  );
};

const OsintPanel = ({ sources, isLoading }) => (
  <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${colors.borderSubtle}`, flexShrink: 0 }}>
      <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontWeight: 700, fontSize: '13px' }}>
        External Intelligence (OSINT)
      </Typography>
      <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '10px', letterSpacing: '0.06em' }}>
        {sources?.length || 0} SOURCES
      </Typography>
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} sx={{ color: colors.accent }} />
        </Box>
      ) : !sources || sources.length === 0 ? (
        <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '12px', px: 2, py: 3, textAlign: 'center' }}>
          No external intelligence captured.
        </Typography>
      ) : (
        sources.map((s) => <SourceCard key={s.id} s={s} />)
      )}
    </Box>
  </Box>
);

export default OsintPanel;
